import { DEFAULT_DATE_FORMAT } from "@/constants";
import { API } from "@/lib/api";
import {
  APIService,
  FetchListRequest,
  FetchListRequestParams,
} from "@/services/api";
import {
  ReadProjectSchedule,
  ReadProjectScheduleContractorVisit,
  CreateProjectSchedule,
  UpdateProjectSchedule,
  WriteProjectScheduleVisit,
} from "@/types/projectSchedule";
import { format } from "date-fns";

type WriteProjectSchedule = CreateProjectSchedule | UpdateProjectSchedule;

const ProjectService = new APIService<
  ReadProjectSchedule,
  WriteProjectSchedule
>(API.Projects);

interface QueryParams<D = string> extends Record<string, D> {
  start: D;
  end: D;
}

export type ProjectSchedulesServiceListQuery = FetchListRequest<
  ReadProjectSchedule,
  QueryParams
>;

export const ProjectSchedulesService = {
  getList: (
    projectId: string,
    fetchParams: FetchListRequestParams<ReadProjectSchedule>,
    queryParams: QueryParams<Date>,
  ) => {
    return ProjectService.getList(
      {
        ...fetchParams,
        queryParams: {
          start: format(queryParams.start, DEFAULT_DATE_FORMAT),
          end: format(queryParams.end, DEFAULT_DATE_FORMAT),
        },
      },
      `/${projectId}/schedules`,
    );
  },

  updateVisit: (
    projectId: string,
    scheduleId: string,
    visitId: string,
    data: WriteProjectScheduleVisit,
  ) => {
    return ProjectService.patch(
      projectId,
      data,
      `schedules/${scheduleId}/${visitId}`,
    );
  },

  createSchedules: (projectId: string, schedules: CreateProjectSchedule[]) => {
    return ProjectService.post<CreateProjectSchedule[]>(
      schedules,
      `${projectId}/schedules`,
    );
  },

  updateSchedules: (projectId: string, schedules: UpdateProjectSchedule[]) => {
    return ProjectService.patch<UpdateProjectSchedule[]>(
      projectId,
      schedules,
      `schedules`,
    );
  },

  deleteSchedules: (
    projectId: string,
    scheduleIds: string[],
    dateRange?: { from_date: string; to_date: string },
  ) => {
    if (scheduleIds.length === 0) {
      return Promise.resolve();
    }

    return ProjectService.delete<{ ids: string[] }>(
      projectId,
      { ids: scheduleIds },
      "schedules",
      dateRange,
    );
  },

  deleteSchedule: (projectId: string, scheduleId: string, visitId: string) => {
    return ProjectService.delete(
      projectId,
      {},
      `schedules/${scheduleId}/${visitId}`,
    );
  },

  cancelVisit: (projectId: string, scheduleId: string, visitId: string) => {
    return ProjectService.patch(
      projectId,
      {},
      `schedules/${scheduleId}/${visitId}/cancel`,
    );
  },

  getContractorVisit: async (
    projectId: string,
    scheduleId: string,
    visitId: string,
  ): Promise<ReadProjectScheduleContractorVisit> => {
    const response = await ProjectService.get<{
      data: ReadProjectScheduleContractorVisit;
    }>(`${projectId}/schedules/${scheduleId}/${visitId}`);

    return response.data;
  },
};
